// components/ManageTransactions.js
import React, { useState, useEffect, useMemo } from 'react';
import { supabase } from '../supabaseClient';
import logo from '../logo2.png';
import SidebarNav from "./SidebarNav";
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

const ManageTransactions = () => {
    const [transactions, setTransactions] = useState([]);
    const [message, setMessage] = useState('');
    const [bankAccounts, setBankAccounts] = useState([]);
    const transactionTypes = ['收入', '支出'];

    // State for filters, sorting, and pagination
    const [filterType, setFilterType] = useState('all');
    const [selectedExpenseType, setSelectedExpenseType] = useState('');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [descriptionKeyword, setDescriptionKeyword] = useState('');
    const [sortField, setSortField] = useState('');
    const [sortOrder, setSortOrder] = useState('asc');
    const [currentPage, setCurrentPage] = useState(1);
    const transactionsPerPage = 10;

    // State for editing transactions
    const [editingTransactionId, setEditingTransactionId] = useState(null);
    const [transactionForm, setTransactionForm] = useState({
        type: '支出',
        transaction_type: '',
        amount: '',
        tax_amount: '', // Added tax_amount
        description: '',
        bank_account_id: '',
        date: '',
    });

    // State for expense types
    const [expenseTypes, setExpenseTypes] = useState([]);

    // State for loading
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        fetchTransactions();
        fetchBankAccounts();
    }, []);

    // Fetch transactions and extract expense types
    const fetchTransactions = async () => {
        setLoading(true);
        const { data, error } = await supabase
            .from('transactions')
            .select('*, bank_accounts(bank_name, account_number)');
        if (!error) {
            setTransactions(data);
            extractExpenseTypes(data);
        } else {
            console.error('Error fetching transactions:', error);
            setMessage('获取交易记录时出错');
        }
        setLoading(false);
    };

    // Extract unique expense types for filtering
    const extractExpenseTypes = (data) => {
        const uniqueExpenseTypes = [...new Set(data
            .filter(t => t.type === 'expense' && t.transaction_type)
            .map(t => t.transaction_type))];
        setExpenseTypes(uniqueExpenseTypes);
    };
    const filteredTransactions = useMemo(() => {
        return transactions.filter((transaction) => {
            // 按类型过滤
            if (filterType !== 'all' && transaction.type !== filterType) {
                return false;
            }

            // 按日期范围过滤
            const transactionDate = new Date(transaction.date);

            if (startDate && transactionDate < new Date(startDate)) {
                return false;
            }

            if (endDate && transactionDate > new Date(endDate)) {
                return false;
            }

            // 按描述关键字过滤
            if (descriptionKeyword && !transaction.description.toLowerCase().includes(descriptionKeyword.toLowerCase())) {
                return false;
            }

            // 按费用类型过滤（仅针对支出）
            if (transaction.type === '支出' && selectedExpenseType && transaction.transaction_type !== selectedExpenseType) {
                return false;
            }

            //费用过滤针对收入
            return !(transaction.type === '收入' && selectedExpenseType && transaction.transaction_type !== selectedExpenseType);


        });
    }, [transactions, filterType, startDate, endDate, descriptionKeyword, selectedExpenseType]);
    // Fetch bank accounts
    const fetchBankAccounts = async () => {
        const { data, error } = await supabase.from('bank_accounts').select('*');
        if (!error) {
            setBankAccounts(data);
        } else {
            console.error('Error fetching bank accounts:', error);
            setMessage('无法获取银行账户信息');
        }
    };

    // Handle filter changes
    const handleFilterTypeChange = (e) => {
        setFilterType(e.target.value);
        setCurrentPage(1); // Reset to first page when filter changes
    };

    const handleExpenseTypeChange = (e) => {
        setSelectedExpenseType(e.target.value);
        setCurrentPage(1);
    };

    const handleStartDateChange = (e) => {
        setStartDate(e.target.value);
        setCurrentPage(1);
    };

    const handleEndDateChange = (e) => {
        setEndDate(e.target.value);
        setCurrentPage(1);
    };

    const handleDescriptionKeywordChange = (e) => {
        setDescriptionKeyword(e.target.value);
        setCurrentPage(1); // Reset to first page when keyword changes
    };

    // Handle sorting (optional)
    const handleSort = (field) => {
        if (sortField === field) {
            setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortOrder('asc');
        }
    };


    // Get current transactions for pagination
    const currentTransactions = useMemo(() => {
        const indexOfLastTransaction = currentPage * transactionsPerPage;
        const indexOfFirstTransaction = indexOfLastTransaction - transactionsPerPage;
        return filteredTransactions.slice(indexOfFirstTransaction, indexOfLastTransaction);
    }, [filteredTransactions, currentPage, transactionsPerPage]);

    const totalPages = Math.ceil(filteredTransactions.length / transactionsPerPage);

    // Calculate totals
    const totalIncome = useMemo(() => {
        return filteredTransactions
            .filter(transaction => transaction.type === '收入')
            .reduce((sum, transaction) => sum + parseFloat(transaction.amount), 0);
    }, [filteredTransactions]);

    const totalExpense = useMemo(() => {
        return filteredTransactions
            .filter(transaction => transaction.type === '支出')
            .reduce((sum, transaction) => sum + parseFloat(transaction.amount), 0);
    }, [filteredTransactions]);

    // Handle editing transactions
    const handleEditTransaction = (transaction) => {
        setEditingTransactionId(transaction.id);
        setTransactionForm({
            type: transaction.type,
            transaction_type: transaction.type === '支出' ? transaction.transaction_type : '',
            amount: transaction.amount,
            tax_amount: transaction.tax_amount || '', // Populate tax_amount
            description: transaction.description,
            bank_account_id: transaction.bank_account_id,
            date: transaction.date.split('T')[0], // Assuming date is in ISO format
        });
    };

    const handleCancelEdit = () => {
        setEditingTransactionId(null);
        setTransactionForm({
            type: '支出',
            transaction_type: '',
            amount: '',
            tax_amount: '', // Reset tax_amount
            description: '',
            bank_account_id: '',
            date: '',
        });
        setMessage('');
    };

    const handleTransactionFormChange = (e) => {
        const { name, value } = e.target;
        setTransactionForm((prevState) => ({
            ...prevState,
            [name]: value,
        }));
    };

    const handleSaveTransaction = async () => {
        // 输入验证
        if (
            !transactionForm.type ||
            (transactionForm.type === '支出' && !transactionForm.transaction_type) ||
            !transactionForm.amount ||
            transactionForm.tax_amount === '' || // Ensure tax_amount is provided
            !transactionForm.description ||
            !transactionForm.bank_account_id ||
            !transactionForm.date
        ) {
            setMessage('请填写所有必填项');
            return;
        }

        const amount = parseFloat(transactionForm.amount);
        const taxAmount = parseFloat(transactionForm.tax_amount);
        if (isNaN(amount) || amount <= 0) {
            setMessage('请输入有效的金额');
            return;
        }
        if (isNaN(taxAmount) || taxAmount < 0) { // Ensure tax_amount is non-negative
            setMessage('请输入有效的税额');
            return;
        }

        // 更新交易记录
        const { error } = await supabase
            .from('transactions')
            .update({
                type: transactionForm.type,
                transaction_type: transactionForm.type === '支出' ? transactionForm.transaction_type : null,
                amount: transactionForm.amount,
                tax_amount: transactionForm.tax_amount, // Update tax_amount
                description: transactionForm.description,
                bank_account_id: transactionForm.bank_account_id,
                date: transactionForm.date,
            })
            .eq('id', editingTransactionId);

        if (!error) {
            setMessage('交易记录更新成功！');
            handleCancelEdit();
            fetchTransactions();
        } else {
            console.error('Error updating transaction:', error);
            setMessage('更新交易记录时出错。');
        }
    };

    // Handle deleting transactions
    const handleDeleteTransaction = async (transactionId) => {
        const confirmation = window.confirm('您确定要删除这条交易记录吗？');
        if (!confirmation) return;

        const { error } = await supabase
            .from('transactions')
            .delete()
            .eq('id', transactionId);

        if (!error) {
            setMessage('交易记录删除成功！');
            fetchTransactions();
        } else {
            console.error('Error deleting transaction:', error);
            setMessage('删除交易记录时出错。');
        }
    };

    // Export to PDF
    const exportToPDF = () => {
        const input = document.getElementById('transactions-table');
        html2canvas(input).then(canvas => {
            const imgData = canvas.toDataURL('image/png');
            const pdf = new jsPDF('p', 'mm', 'a4');
            pdf.addImage(logo, 'PNG', 10, 10, 50, 20);
            pdf.text('Transactions Report', 70, 20);
            pdf.addImage(imgData, 'PNG', 10, 40, 190, 0);
            pdf.save('transactions_report.pdf');
        });
    };

    // Handle page change
    const handlePageChange = (pageNumber) => {
        setCurrentPage(pageNumber);
    };

    return (
        <div className="min-h-screen bg-gray-50">
            <SidebarNav />

            <main className="lg:pl-64 p-6">
                <div className="mb-8">
                    <div className="flex items-center justify-between">
                        <img
                            src={logo}
                            alt="Kindbridge Investments"
                            className="h-12 w-auto"
                        />
                    </div>
                    <h1 className="text-2xl font-semibold text-gray-900">管理交易记录</h1>
                </div>

                {message && (
                    <div className="mb-4 p-4 bg-blue-50 text-blue-700 rounded-lg">
                        {message}
                    </div>
                )}

                {/* Filters */}
                <div className="bg-white rounded-lg shadow-sm p-6 mb-6 border border-gray-100">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                        <div>
                            <label htmlFor="filter-type" className="block text-sm font-medium text-gray-700 mb-1">
                                交易类型
                            </label>
                            <select
                                id="filter-type"
                                value={filterType}
                                onChange={handleFilterTypeChange}
                                className="block w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            >
                                <option value="all">所有类型</option>
                                <option value="收入">收入</option>
                                <option value="支出">支出</option>
                                <option value="工资">工资</option>
                            </select>
                        </div>

                        <div>
                            <label htmlFor="expense-type" className="block text-sm font-medium text-gray-700 mb-1">
                                费用类型
                            </label>
                            <select
                                id="expense-type"
                                value={selectedExpenseType}
                                onChange={handleExpenseTypeChange}
                                className="block w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            >
                                <option value="">所有费用类型</option>
                                {expenseTypes.map((type, index) => (
                                    <option key={index} value={type}>{type}</option>
                                ))}
                            </select>
                        </div>

                        <div>
                            <label htmlFor="start-date" className="block text-sm font-medium text-gray-700 mb-1">
                                开始日期
                            </label>
                            <input
                                type="date"
                                id="start-date"
                                value={startDate}
                                onChange={handleStartDateChange}
                                className="block w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            />
                        </div>

                        <div>
                            <label htmlFor="end-date" className="block text-sm font-medium text-gray-700 mb-1">
                                结束日期
                            </label>
                            <input
                                type="date"
                                id="end-date"
                                value={endDate}
                                onChange={handleEndDateChange}
                                className="block w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            />
                        </div>

                        <div>
                            <label htmlFor="description-keyword" className="block text-sm font-medium text-gray-700 mb-1">
                                搜索描述
                            </label>
                            <input
                                type="text"
                                id="description-keyword"
                                value={descriptionKeyword}
                                onChange={handleDescriptionKeywordChange}
                                placeholder="搜索描述"
                                className="block w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            />
                        </div>
                    </div>
                </div>

                {/* Transactions Table */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-100">
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                                    onClick={() => handleSort('type')}>
                                    类型 {sortField === 'type' ? (sortOrder === 'asc' ? '↑' : '↓') : ''}
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                                    onClick={() => handleSort('transaction_type')}>
                                    子类型 {sortField === 'transaction_type' ? (sortOrder === 'asc' ? '↑' : '↓') : ''}
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                                    onClick={() => handleSort('amount')}>
                                    不含税金额 {sortField === 'amount' ? (sortOrder === 'asc' ? '↑' : '↓') : ''}
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                                    onClick={() => handleSort('tax_amount')}>
                                    税额 {sortField === 'tax_amount' ? (sortOrder === 'asc' ? '↑' : '↓') : ''}
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                                    onClick={() => handleSort('description')}>
                                    描述 {sortField === 'description' ? (sortOrder === 'asc' ? '↑' : '↓') : ''}
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                                    onClick={() => handleSort('bank_account')}>
                                    银行账户 {sortField === 'bank_account' ? (sortOrder === 'asc' ? '↑' : '↓') : ''}
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                                    onClick={() => handleSort('date')}>
                                    日期 {sortField === 'date' ? (sortOrder === 'asc' ? '↑' : '↓') : ''}
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">操作</th>
                            </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                            {currentTransactions.map((transaction) => (
                                <tr key={transaction.id} className="hover:bg-gray-50">
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        {editingTransactionId === transaction.id ? (
                                            <select
                                                name="type"
                                                value={transactionForm.type}
                                                onChange={handleTransactionFormChange}
                                                className="block w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                            >
                                                <option value="收入">收入</option>
                                                <option value="支出">支出</option>
                                            </select>
                                        ) : (
                                            transaction.type
                                        )}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                        {editingTransactionId === transaction.id ? (
                                            <input
                                                type="text"
                                                name="transaction_type"
                                                value={transactionForm.transaction_type}
                                                onChange={handleTransactionFormChange}
                                                className="block w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                            />
                                        ) : (
                                            transaction.transaction_type || 'N/A'
                                        )}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        {editingTransactionId === transaction.id ? (
                                            <input
                                                type="number"
                                                name="amount"
                                                value={transactionForm.amount}
                                                onChange={handleTransactionFormChange}
                                                className="block w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                                min="0"
                                                step="0.01"
                                            />
                                        ) : (
                                            `R ${parseFloat(transaction.amount).toFixed(2)}`
                                        )}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        {editingTransactionId === transaction.id ? (
                                            <input
                                                type="number"
                                                name="tax_amount"
                                                value={transactionForm.tax_amount}
                                                onChange={handleTransactionFormChange}
                                                className="block w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                                min="0"
                                                step="0.01"
                                            />
                                        ) : (
                                            transaction.tax_amount ? `R ${parseFloat(transaction.tax_amount).toFixed(2)}` : 'N/A'
                                        )}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                        {editingTransactionId === transaction.id ? (
                                            <input
                                                type="text"
                                                name="description"
                                                value={transactionForm.description}
                                                onChange={handleTransactionFormChange}
                                                className="block w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                            />
                                        ) : (
                                            transaction.description
                                        )}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                        {editingTransactionId === transaction.id ? (
                                            <select
                                                name="bank_account_id"
                                                value={transactionForm.bank_account_id}
                                                onChange={handleTransactionFormChange}
                                                className="block w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                            >
                                                <option value="">选择银行账户</option>
                                                {bankAccounts.map((account) => (
                                                    <option key={account.id} value={account.id}>
                                                        {account.bank_name} - {account.account_number}
                                                    </option>
                                                ))}
                                            </select>
                                        ) : (
                                            transaction.bank_accounts
                                                ? `${transaction.bank_accounts.bank_name} - ${transaction.bank_accounts.account_number}`
                                                : 'N/A'
                                        )}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                        {editingTransactionId === transaction.id ? (
                                            <input
                                                type="date"
                                                name="date"
                                                value={transactionForm.date}
                                                onChange={handleTransactionFormChange}
                                                className="block w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                            />
                                        ) : (
                                            new Date(transaction.date).toLocaleDateString()
                                        )}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                        {editingTransactionId === transaction.id ? (
                                            <div className="flex space-x-2">
                                                <button
                                                    onClick={handleSaveTransaction}
                                                    className="px-3 py-1 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                                                >
                                                    保存
                                                </button>
                                                <button
                                                    onClick={handleCancelEdit}
                                                    className="px-3 py-1 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
                                                >
                                                    取消
                                                </button>
                                            </div>
                                        ) : (
                                            <div className="flex space-x-2">
                                                <button
                                                    onClick={() => handleEditTransaction(transaction)}
                                                    className="px-3 py-1 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                                                >
                                                    编辑
                                                </button>
                                                <button
                                                    onClick={() => handleDeleteTransaction(transaction.id)}
                                                    className="px-3 py-1 bg-red-600 text-white rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
                                                >
                                                    删除
                                                </button>
                                            </div>
                                        )}
                                    </td>
                                </tr>
                            ))}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination */}
                    <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200">
                        <div className="flex justify-center w-full space-x-2">
                            {Array.from({ length: totalPages }, (_, index) => (
                                <button
                                    key={index + 1}
                                    onClick={() => handlePageChange(index + 1)}
                                    className={`px-3 py-1 rounded-md text-sm font-medium
                                        ${currentPage === index + 1
                                        ? 'bg-blue-600 text-white'
                                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                    } focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2`}
                                >
                                    {index + 1}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            </main>
        </div>
    );
}

export default ManageTransactions;
